import React, {useEffect, useMemo, useState} from 'react';
import PageHeader from '../../Component/Layout/PageHeader';
import {useNavigate, useLocation} from 'react-router-dom';
import LanguageHeader from '../../Component/Layout/LanguageHeader';
import {getTranslations} from '../Actions/getTranslations';
import {getTenantLanguages} from '../Actions/getLanguages';
import {saveTranslateData} from '../Actions/saveTranslation';
import AppLoader from '../../HelperClasses/AppLoaderClasses/AppLoader';
import AppConstants from '../../Constants/AppConstants';
import {useLocale} from '../../Localization/LocaleContext';

const readDraft = key => {
  try { return JSON.parse(window.localStorage.getItem(key) || '{}') || {}; }
  catch (error) { return {}; }
};
const writeDraft = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); }
  catch (error) { /* Storage is optional; keep the editor usable. */ }
};
const clearDraft = key => {
  try { window.localStorage.removeItem(key); }
  catch (error) { /* Ignore storage errors after save or explicit discard. */ }
};

const CategoryDescriptionTranslate = ({
  translationTitle,
  titleString,
  translationRef,
  backBtnAction,
}) => {
  const navigate = useNavigate();
  const {search} = useLocation();
  const {translate: t} = useLocale();
  const categoryID = useMemo(() => new URLSearchParams(search).get('ct_id') || 'new', [search]);
  const draftKey = `arcticwhale:${AppConstants.tenantName || 'current-tenant'}:${AppConstants.userId || 'current-user'}:category-translations:${categoryID}`;

  const [category_form, setCategoryForm] = useState({
    description: {},
    name: {},
  });
  const [draftReady, setDraftReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // language header
  const [selected_language, setSelectedLanguage] = useState([]);
  const [default_language, setDefaultLanguage] = useState(null);
  const [tenant_languages, setTenantLanguages] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getTenantLanguages()
      .then(res => {
        setIsLoading(false);

        setTenantLanguages(res);
      })
      .catch(err => {
        setIsLoading(false);
        console.log(err);
      });
  }, []);

  useEffect(() => {
    const defaultCode = tenant_languages?.find(lan => lan?.default)?.code || tenant_languages?.[0]?.code || null;
    setSelectedLanguage(defaultCode);
    setDefaultLanguage(defaultCode);
  }, [tenant_languages]);

  //
  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const currentCategoryID = urlParams.get('ct_id');
    if (!currentCategoryID) return;
    setDraftReady(false);
    const savedDraft = readDraft(draftKey);
    setCategoryForm(savedDraft);
    setIsLoading(true);
    Promise.all(['name', 'description'].map(translation_type => getTranslations({
      reference_id: currentCategoryID,
      reference_type: 'category',
      translation_type,
    }))).then(([nameResponse, descriptionResponse]) => {
      const valuesFor = (response, field) => ({
        ...(response?.values || []).reduce((acc, curr) => ({...acc, [curr?.locale]: curr?.value}), {}),
        ...(savedDraft[field] || {}),
      });
      setCategoryForm({name: valuesFor(nameResponse, 'name'), description: valuesFor(descriptionResponse, 'description')});
      setDraftReady(true);
      setIsLoading(false);
    }).catch(() => {
      setCategoryForm(savedDraft);
      setDraftReady(true);
      setIsLoading(false);
    });
  }, [search, draftKey]);

  useEffect(() => {
    if (categoryID !== 'new' && draftReady) writeDraft(draftKey, category_form);
  }, [categoryID, category_form, draftKey, draftReady]);

  // on Update value
  const onUpdateValue = ({key, value}) => {
    if (key === 'description' && selected_language) {
      setCategoryForm(prev => ({
        ...prev,
        description: {
        ...prev.description,
          [selected_language]: value,
        },
      }));
    }
    if (key === 'name' && selected_language) {
      setCategoryForm(prev => ({
        ...prev,
        name: {
        ...prev.name,
          [selected_language]: value,
        },
      }));
    }
  };

  //
  const onSave = async () => {
    const currentCategoryID = new URLSearchParams(search).get('ct_id');
    if (!currentCategoryID) return;
    setIsLoading(true);
    const save = config => new Promise(resolve => saveTranslateData({...config, closeFunc: () => resolve(true), onErrorHappen: () => resolve(false)}));
    const nameSaved = await save({reference_id: currentCategoryID, reference_type: 'category', translation_type: 'name', translations_data: category_form.name});
    if (nameSaved) {
      const descriptionSaved = await save({reference_id: currentCategoryID, reference_type: 'category', translation_type: 'description', translations_data: category_form.description});
      if (descriptionSaved) {
        clearDraft(draftKey);
        setIsLoading(false);
        navigate('/listing-category');
        return;
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="">
      <PageHeader
        title={translationTitle || t('category.translation_title', 'Translate Category')}
        close_action={() => { clearDraft(draftKey); navigate('/listing-category'); }}
        save_action={() => onSave()}
      />
      <div>
        {/* Language Header */}
        <LanguageHeader
          selected_language={selected_language}
          setSelectedLanguage={setSelectedLanguage}
          setDefaultLanguage={setDefaultLanguage}
          languages={tenant_languages}
        />
        <div className="space-y-4">
          <div className=" flex flex-col w-full gap-2 mt-4 ">
            <label htmlFor="ct-name" className=" text-lg text-black">
              {t('category.translation_category_name', 'Category Name')}
            </label>
            <input
              type="text"
              value={category_form?.name?.[selected_language] ?? ''}
              name="ct-name"
              className=" border rounded-md border-[#e6e7e7]"
              onChange={e => {
                onUpdateValue({key: 'name', value: e.target.value});
              }}
            />
          </div>
          {/* Desc */}
          <div className=" flex flex-col w-full gap-2 mt-4 ">
            <label htmlFor="ct-desc" className=" text-lg text-black">
              {t('category.translation_description', 'Description')}
            </label>
            <textarea
              value={category_form?.description?.[selected_language] ?? ''}
              name="ct-desc"
              id="ct-desc"
              cols="30"
              rows="10"
              className=" border rounded-md border-[#e6e7e7]"
              onChange={e => {
                onUpdateValue({key: 'description', value: e.target.value});
              }}
            />
          </div>
        </div>
      </div>

      <AppLoader enable={!isLoading} />
    </div>
  );
};

export default CategoryDescriptionTranslate;
